import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons';
import {
    ICorporationResponse,
    IHardwareStatusSummaryResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { InfiniteDataSourceTable } from 'components/infinite_data_source_table/InfiniteDataSourceTable';
import {
    TableExternalLinkIcon,
    TableLink,
} from 'components/infinite_data_source_table/styled';
import { Column } from 'components/infinite_data_source_table/types';
import { NoValue } from 'components/no_value/NoValue';
import { useInfiniteCorporationHardwareStatusDataSource } from 'custom_hooks/data_hooks/data_source/corporationHardwareStatusDataSource';
import { useLocationFavorites } from 'custom_hooks/data_hooks/favorites/useLocationFavorites';
import {
    Container,
    CountText,
    PercentText,
    Title,
} from 'pages/Corporation/hardware_status/styled';
import { CorporationOverviewFilters } from 'pages/Corporation/types';
import { forwardRef, memo, useImperativeHandle, useMemo } from 'react';
import { getHardwareRoute } from 'utils/RouteHelper';

const HARDWARE_TYPES = [
    { label: 'Base Stations', icon: 'baseStation', sdkKey: 'Collectors' },
    { label: 'Repeaters', icon: 'repeater', sdkKey: 'Repeaters' },
    { label: 'Sensors', icon: 'sensor', sdkKey: 'Nodes' },
    { label: 'IoT Hubs', icon: 'hub', sdkKey: 'Hubs' },
    { label: 'Piezo Sensing', icon: 'piezoSensing', sdkKey: 'PiezoNodes' },
] as const;

const PAGE_LIMIT = 20;

function formatHardwareCount(count: number, total: number): JSX.Element {
    const percentage = Math.round((count / total) * 100);
    return (
        <>
            <CountText>{`${count}/${total}`}</CountText>
            <PercentText>{`(${percentage}%)`}</PercentText>
        </>
    );
}

export type CorporationHardwareStatusWidgetProps = {
    corporation: ICorporationResponse;
    filters?: CorporationOverviewFilters;
};

export type CorporationHardwareStatusWidgetRef = {
    refetch: () => Promise<unknown>;
};

function CorporationHardwareStatusWidget(
    { corporation, filters }: CorporationHardwareStatusWidgetProps,
    ref: React.ForwardedRef<CorporationHardwareStatusWidgetRef>
) {
    const favorites = useLocationFavorites({
        corporationId: corporation.Id,
        locationId: undefined,
    });
    const locationIds = useMemo(
        () =>
            filters?.favoritesFilter === 'favorites'
                ? favorites.map((fav) => fav.FavoriteId)
                : undefined,
        [favorites, filters?.favoritesFilter]
    );

    const dataSource = useInfiniteCorporationHardwareStatusDataSource({
        name: 'corporation-online-hardware',
        defaultFilter: {
            corporationId: corporation.Id,
            locationIds,
        },
        keepPreviousData: true,
        pageLimit: PAGE_LIMIT,
        enabled: true,
    });

    useImperativeHandle(ref, () => ({
        refetch: dataSource.refetch,
    }));

    const columns = useMemo<Column<IHardwareStatusSummaryResponse>[]>(() => {
        const locationColumn: Column<IHardwareStatusSummaryResponse> = {
            id: 'name',
            header: () => 'Location',
            render: ({ rowData }) => (
                <TableLink
                    to={getHardwareRoute(corporation.Id, rowData.Location.LocationId)}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {rowData.Location.LocationName}
                    <TableExternalLinkIcon icon={faExternalLinkAlt} aria-hidden="true" />
                </TableLink>
            ),
        };

        // Check if any location has any hardware at all
        const hasAnyHardware = dataSource.results?.some((location) =>
            HARDWARE_TYPES.some((type) => location[type.sdkKey].TotalCount > 0)
        );

        // If no hardware at all, show all columns
        if (!hasAnyHardware) {
            const allHardwareColumns = HARDWARE_TYPES.map(
                (type): Column<IHardwareStatusSummaryResponse> => ({
                    id: type.sdkKey,
                    header: () => type.label,
                    render: () => <NoValue />,
                })
            );
            return [locationColumn, ...allHardwareColumns];
        }

        // Otherwise, only show columns that have hardware
        const hardwareColumns = HARDWARE_TYPES.filter((type) =>
            dataSource.results?.some((loc) => loc[type.sdkKey].TotalCount > 0)
        ).map(
            (type): Column<IHardwareStatusSummaryResponse> => ({
                id: type.sdkKey,
                header: () => type.label,
                render: ({ rowData }) => {
                    const data = rowData[type.sdkKey];
                    if (!data.TotalCount) {
                        return <NoValue />;
                    }
                    return formatHardwareCount(data.OnlineCount, data.TotalCount);
                },
            })
        );

        return [locationColumn, ...hardwareColumns];
    }, [corporation.Id, dataSource.results]);

    const hasAnyHardware = useMemo(
        () =>
            dataSource.results?.some((location) =>
                HARDWARE_TYPES.some((type) => location[type.sdkKey].TotalCount > 0)
            ),
        [dataSource.results]
    );

    const getRowKey = (row: IHardwareStatusSummaryResponse) => row.Location.LocationId;

    return (
        <Container>
            <Title>Online Assigned Hardware</Title>
            <InfiniteDataSourceTable
                dataSource={dataSource}
                columns={columns}
                rowKey={getRowKey}
                noResultsMessage="No hardware found at this corporation"
                showEmptyState={!hasAnyHardware}
            />
        </Container>
    );
}

const memoCorporationHardwareStatusWidget = memo(
    forwardRef<CorporationHardwareStatusWidgetRef, CorporationHardwareStatusWidgetProps>(
        CorporationHardwareStatusWidget
    )
);
export { memoCorporationHardwareStatusWidget as CorporationHardwareStatusWidget };
